import React, { useEffect, useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { listProducts } from '../Actions/productActions';
import { listCategories } from '../Actions/categoryActions';
import LoadingBox from '../Components/LoadingBox';
import MessageBox from '../Components/MessageBox';
import Product from '../Components/Product';
import CategoryFilter from '../Components/CategoryFilter';
import { useSearch } from '../Context/SearchContext';

export default function HomeScreen() {

const dispatch = useDispatch();
const productList = useSelector((state) => state.productList);
const {loading, error, products} = productList;

// read role (supports either Role or role casing)
const userSignin = useSelector((state) => state.userSignin);
const signedInRole = (userSignin?.userInfo?.Role || userSignin?.userInfo?.role || '').toString().toLowerCase();

const categoryList = useSelector((state) => state.categoryList);
const {loading: categoriesLoading, categories: categoriesData} = categoryList;

const [selectedCategory, setSelectedCategory] = useState('All');
const [currentPage, setCurrentPage] = useState(1);
const { searchQuery } = useSearch();
const itemsPerPage = 20;
  
  const history = useHistory();
  const location = useLocation();

  // If auth action emails point to the root URL with query params, forward to specific screens
  useEffect(() => {
    const search = location.search || '';
    if (!search) return;
    const params = new URLSearchParams(search);
    const mode = params.get('mode');
    const oob = params.get('oobCode');
    if (mode === 'resetPassword' && oob) {
      history.replace(`/reset-password?${params.toString()}`);
    } else if (mode === 'verifyEmail' && oob) {
      history.replace(`/verify-email?${params.toString()}`);
    }
  }, [location.search, history]);

  useEffect(() => {
    dispatch(listProducts());
    dispatch(listCategories());
  },[dispatch]);

  // Extract unique categories from products
  const categories = useMemo(() => {
    if (!products || products.length === 0) return [];
    const uniqueCategories = [...new Set(products.map(product => product.category))];
    return uniqueCategories.filter(category => category); // Remove any null/undefined categories
  }, [products]);

  // Filter products based on role (productFor) + selected category + search query
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    
    let filtered = products;
    
    // Role-based visibility
    filtered = filtered.filter(p => {
      const pf = (p.productFor || 'user').toString().toLowerCase();
      if (!signedInRole) {
        // Not logged in: only show products for user
        return pf === 'user';
      }
      if (signedInRole === 'retailer') return pf === 'retailer';
      // default to user
      return pf === 'user';
    });

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    
    // Filter by search query (search in title, tags, category, brand)
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product => {
        const title = product.product_title?.toLowerCase() || '';
        const category = product.category?.toLowerCase() || '';
        const brand = product.brand?.toLowerCase() || '';
        const description = product.product_description?.toLowerCase() || '';
        
        // Handle tags safely - could be string, array, or other types
        let tags = '';
        if (product.tags) {
          if (typeof product.tags === 'string') {
            tags = product.tags.toLowerCase();
          } else if (Array.isArray(product.tags)) {
            tags = product.tags.join(' ').toLowerCase();
          } else {
            tags = String(product.tags).toLowerCase();
          }
        }
        
        return title.includes(query) || 
               category.includes(query) || 
               brand.includes(query) || 
               tags.includes(query) ||
               description.includes(query);
      });
    }
    
    // Sort by latest (newest first) - using added_on timestamp or id
    filtered = [...filtered].sort((a, b) => {
      // Try added_on timestamp first
      const dateA = a.added_on?.toDate?.() || a.added_on || a.createdAt?.toDate?.() || a.createdAt || 0;
      const dateB = b.added_on?.toDate?.() || b.added_on || b.createdAt?.toDate?.() || b.createdAt || 0;
      
      if (dateA && dateB) {
        return new Date(dateB) - new Date(dateA);
      }
      // Fallback: sort by id (assuming newer products have later IDs)
      return (b.id || '').localeCompare(a.id || '');
    });
    
    return filtered;
  }, [products, selectedCategory, searchQuery]);

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page when category changes
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset to first page when search query changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

    return (
        <div>
        { loading ? (
          <LoadingBox></LoadingBox>
        ): error ? (
          <MessageBox variant='danger'>{error}</MessageBox>
        ) : (
          <div>
            <CategoryFilter 
              categories={categories}
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategorySelect}
              categoriesData={categoriesData}
            />
            <div className="products-section">
              <div className="products-header">
                <h2 className="products-title">
                  {selectedCategory === 'All' ? 'All Products' : selectedCategory}
                  <span className="products-count">({filteredProducts.length} items)</span>
                </h2>
                
                {/* Pagination Info */}
                {filteredProducts.length > 0 && (
                  <div className="pagination-info">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} products
                    {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                  </div>
                )}
                
                <div className="products-grid">
                  {paginatedProducts.map((product) => (
                    <Product key={product.id } product={product} />
                  ))}
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="pagination-controls">
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>
                    
                    <div className="pagination-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          className={`pagination-number ${page === currentPage ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                    
                    <button 
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next →
                    </button>
                  </div>
                )}
                
                {filteredProducts.length === 0 && (
                  <div className="no-products">
                    <p>No products found in this category.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )} 
      </div>
    );
}